import { useState, useEffect } from "react";
import {
  InsightsCard,
  InsightsDividerList,
  InsightsDividerRow,
  MaskedAccountNumber,
  formatDate,
  formatCurrency,
} from "./InsightsCard";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { decryptPersonData } from "@/shared/utils/serverEncryption";
import { useCitation, getCitationForField } from "./CitationContext";

export interface CreditCardStatementExtraction {
  issuer_name?: string;
  card_network?: string;
  account_number?: string;
  current_balance?: number;
  credit_limit?: number;
  minimum_payment?: number;
  payment_due_date?: string;
  statement_date?: string;
}

export default function CreditCardStatementInsights({
  data,
  citations,
}: {
  data: CreditCardStatementExtraction;
  citations?: Record<string, unknown> | null;
}) {
  const { showCitation } = useCitation();

  const handleFieldClick = (fieldPath: string) => {
    const citation = getCitationForField(citations, fieldPath);
    if (citation) showCitation(citation);
  };

  const {
    issuer_name,
    card_network,
    account_number,
    current_balance,
    credit_limit,
    minimum_payment,
    payment_due_date,
    statement_date,
  } = data;

  const { person } = useAppContext();

  const [decryptedAccountNumber, setDecryptedAccountNumber] = useState<string | null>(null);
  const [isLoadingDecryption, setIsLoadingDecryption] = useState(false);

  useEffect(() => {
    if (!person?.estate_id || !account_number) {
      return;
    }

    const decryptAccountNumber = async () => {
      setIsLoadingDecryption(true);
      try {
        const decrypted = await decryptPersonData(
          person.estate_id,
          account_number,
          "account_number",
        );
        setDecryptedAccountNumber(decrypted);
      } catch (error) {
        console.error("Failed to decrypt account number:", error);
        setDecryptedAccountNumber(null);
      }
      setIsLoadingDecryption(false);
    };

    decryptAccountNumber();
  }, [account_number, person?.estate_id]);

  return (
    <div className="px-6 pb-6 space-y-4">
      <InsightsCard title="Card Information" noPadding>
        <InsightsDividerList>
          {issuer_name && (
            <InsightsDividerRow
              label="Issuer"
              value={issuer_name}
              onClick={
                getCitationForField(citations, "issuer_name")
                  ? () => handleFieldClick("issuer_name")
                  : undefined
              }
            />
          )}
          {card_network && (
            <InsightsDividerRow
              label="Card Network"
              value={card_network}
              onClick={
                getCitationForField(citations, "card_network")
                  ? () => handleFieldClick("card_network")
                  : undefined
              }
            />
          )}
          <InsightsDividerRow
            label="Account Number"
            value={
              <MaskedAccountNumber value={decryptedAccountNumber} isLoading={isLoadingDecryption} />
            }
            onClick={
              getCitationForField(citations, "account_number")
                ? () => handleFieldClick("account_number")
                : undefined
            }
          />
          {statement_date && (
            <InsightsDividerRow
              label="Statement Date"
              value={formatDate(statement_date)}
              onClick={
                getCitationForField(citations, "statement_date")
                  ? () => handleFieldClick("statement_date")
                  : undefined
              }
            />
          )}
        </InsightsDividerList>
      </InsightsCard>

      <InsightsCard title="Balance & Payment" noPadding>
        <InsightsDividerList>
          {current_balance !== undefined && (
            <InsightsDividerRow
              label="Current Balance"
              value={formatCurrency(current_balance)}
              onClick={
                getCitationForField(citations, "current_balance")
                  ? () => handleFieldClick("current_balance")
                  : undefined
              }
            />
          )}
          {credit_limit !== undefined && (
            <InsightsDividerRow
              label="Credit Limit"
              value={formatCurrency(credit_limit)}
              onClick={
                getCitationForField(citations, "credit_limit")
                  ? () => handleFieldClick("credit_limit")
                  : undefined
              }
            />
          )}
          {minimum_payment !== undefined && (
            <InsightsDividerRow
              label="Minimum Payment"
              value={formatCurrency(minimum_payment)}
              onClick={
                getCitationForField(citations, "minimum_payment")
                  ? () => handleFieldClick("minimum_payment")
                  : undefined
              }
            />
          )}
          {payment_due_date && (
            <InsightsDividerRow
              label="Payment Due Date"
              value={formatDate(payment_due_date)}
              onClick={
                getCitationForField(citations, "payment_due_date")
                  ? () => handleFieldClick("payment_due_date")
                  : undefined
              }
            />
          )}
        </InsightsDividerList>
      </InsightsCard>
    </div>
  );
}
